<template>
  <div class="leo-operator-assistant">
    <el-drawer
      title="Leo SOC助手"
      :visible.sync="drawerOpen"
      size="420px"
      direction="rtl"
      append-to-body
    >
      <p class="muted assistant-hint">
        问后台操作、训练 SOP、何时找工程。已感知当前页面，会优先给本页相关说明。
      </p>
      <p v-if="pagePreface" class="assistant-page-preface">{{ pagePreface }}</p>
      <div v-if="clarifyOptions.length" class="assistant-clarify">
        <p class="assistant-clarify-prompt">{{ clarifyPrompt }}</p>
        <el-button
          v-for="(opt, idx) in clarifyOptions"
          :key="idx"
          size="mini"
          type="primary"
          plain
          @click="askClarifyOption(opt)"
        >{{ opt.label }}</el-button>
      </div>
      <div class="assistant-topics">
        <el-button
          v-for="t in quickTopics"
          :key="t.id"
          size="mini"
          plain
          :loading="loading && question === t.question"
          @click="askQuick(t.question)"
        >{{ t.labelZh }}</el-button>
      </div>
      <el-input
        v-model="question"
        type="textarea"
        :rows="3"
        placeholder="输入问题，例如：Composer shadow 怎么办？"
      />
      <el-button
        type="primary"
        size="small"
        style="margin-top: 8px"
        :loading="loading"
        @click="ask"
      >提问</el-button>
      <div v-if="answer" class="assistant-answer">
        <div class="assistant-answer-title">回答</div>
        <pre class="assistant-answer-body">{{ answer }}</pre>
        <div v-if="answer" class="assistant-feedback">
          <el-button size="mini" plain @click="sendFeedback(false)">回答不对</el-button>
        </div>
        <p v-if="disclaimer" class="muted">{{ disclaimer }}</p>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  fetchOperatorSupportTopics,
  askOperatorSupport,
  recordOperatorPageView,
  feedbackOperatorSupport,
  buildOperatorPageContext
} from '@/api/leoTrainingOperatorSupport'
import { leoOperatorAssistantBus } from '@/utils/leoOperatorAssistant'

export default {
  name: 'LeoTrainingOperatorAssistant',
  data () {
    return {
      drawerOpen: false,
      loading: false,
      question: '',
      answer: '',
      disclaimer: '',
      pagePreface: '',
      quickTopics: [],
      clarifyPrompt: '',
      clarifyOptions: [],
      clarifySlot: ''
    }
  },
  watch: {
    '$route.fullPath' () {
      this.onRouteChange()
    },
    drawerOpen (open) {
      if (open) {
        this.refreshTopics()
      }
    }
  },
  mounted () {
    leoOperatorAssistantBus.$on('open', this.open)
    leoOperatorAssistantBus.$on('close', this.close)
    this.reportPageContext()
  },
  beforeDestroy () {
    leoOperatorAssistantBus.$off('open', this.open)
    leoOperatorAssistantBus.$off('close', this.close)
  },
  methods: {
    pageContext () {
      return buildOperatorPageContext(this.$route, 'soc_admin')
    },
    open () {
      this.drawerOpen = true
    },
    close () {
      this.drawerOpen = false
    },
    onRouteChange () {
      this.reportPageContext()
      if (this.drawerOpen) {
        this.refreshTopics()
      }
    },
    async reportPageContext () {
      try {
        await recordOperatorPageView(this.pageContext())
      } catch (e) {
        // non-blocking
      }
    },
    async refreshTopics () {
      try {
        const res = await fetchOperatorSupportTopics(this.pageContext())
        const data = res && res.data
        this.quickTopics = (data && data.quickTopics) || []
        this.pagePreface = (data && data.pagePrefaceZh) || ''
      } catch (e) {
        this.quickTopics = []
        this.pagePreface = ''
      }
    },
    askQuick (q) {
      this.question = q
      this.ask()
    },
    askClarifyOption (opt) {
      const label = (opt && opt.label) || ''
      if (!label) return
      this.question = label
      this.ask()
    },
    clearClarify () {
      this.clarifyPrompt = ''
      this.clarifyOptions = []
      this.clarifySlot = ''
    },
    async ask () {
      const q = (this.question || '').trim()
      if (!q) {
        this.$message.warning('请输入问题')
        return
      }
      this.loading = true
      this.clearClarify()
      try {
        const res = await askOperatorSupport({
          question: q,
          pageContext: this.pageContext()
        })
        const data = res && res.data
        if (data && data.phase === 'clarify') {
          this.answer = ''
          this.clarifyPrompt = data.clarifyPrompt || data.answerZh || ''
          this.clarifySlot = data.clarifySlot || ''
          this.clarifyOptions = data.clarifyOptions || []
          this.disclaimer = data.disclaimerZh || ''
          if (data.pagePrefaceZh) {
            this.pagePreface = data.pagePrefaceZh
          }
          return
        }
        this.answer = (data && data.answerZh) || '暂无回答'
        this.disclaimer = (data && data.disclaimerZh) || ''
        if (data && data.pagePrefaceZh) {
          this.pagePreface = data.pagePrefaceZh
        }
      } catch (e) {
        this.$message.error(e.message || '提问失败')
      } finally {
        this.loading = false
      }
    },
    async sendFeedback (helpful) {
      if (!this.answer) return
      try {
        await feedbackOperatorSupport({
          question: this.question,
          answerZh: this.answer,
          helpful: helpful,
          reason: helpful ? '' : 'user_marked_wrong',
          pageContext: this.pageContext()
        })
        this.$message.success(helpful ? '感谢反馈' : '已记录，将纳入训练')
      } catch (e) {
        this.$message.error('反馈失败')
      }
    }
  }
}
</script>

<style scoped>
.assistant-hint {
  font-size: 13px;
  margin: 0 0 12px;
}
.assistant-page-preface {
  font-size: 13px;
  color: #409eff;
  margin: 0 0 12px;
  line-height: 1.5;
}
.assistant-clarify {
  margin-bottom: 12px;
  padding: 10px;
  background: #fef9e7;
  border-radius: 6px;
  border: 1px solid #faecd8;
}
.assistant-clarify-prompt {
  font-size: 13px;
  margin: 0 0 8px;
  line-height: 1.5;
}
.assistant-topics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.assistant-answer {
  margin-top: 16px;
}
.assistant-answer-title {
  font-weight: 600;
  margin-bottom: 8px;
}
.assistant-answer-body {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 6px;
  max-height: 360px;
  overflow: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/LeoKnowledge/LeoTrainingOperatorAssistant.vue